import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import { usePublishProgress } from "@/components/PublishProgress";
import {
  FilterBar,
  filtersToBody,
  hasAnyFilter,
  resolveTimeRange,
  TimeRangeDropdown,
  type Filters,
  type MarketListing,
  type TimeRangeValue,
} from "@/components/MarketShared";
import type { TeamMember } from "@/types";

interface OwnerCandidate {
  score: number;
  /** Live scan (Bulk page): cadastral | fingerprint_tight | fingerprint |
   *  self. Frozen scan (Competition agency drill-in): dedup | heuristic |
   *  self. Both render through the same badge. */
  method: string;
  reasons: string[];
  listing: MarketListing;
}

interface FinderItem {
  competitor_listing: MarketListing;
  top_candidate: OwnerCandidate | null;
  candidate_count: number;
}

interface FinderResponse {
  items: FinderItem[];
  total: number;
  /** Set when the server stopped counting at its ceiling — ``total`` is then
   *  a floor ("10,000+"), so the sweep must page until a short page instead
   *  of until ``total``. Counting every matching row of a 12 GB table is what
   *  tripped the statement timeout on wide scans. */
  total_capped?: boolean;
  /** False when the server gave up counting inside its budget — the sweep
   *  must then page until a short page, exactly as for a capped total. */
  total_exact?: boolean;
  processed: number;
  matched: number;
  /** Keyset cursor for the next page (live market scan only; the frozen
   *  agency endpoint doesn't issue one). Paging on it instead of bumping
   *  ``offset`` is what keeps a deep sweep affordable — an OFFSET makes the
   *  server re-read and re-sort every row before the one you asked for, and
   *  at offset 250 with a narrow filter that was enough to trip the 30s
   *  statement timeout on every attempt, which read as "re-scan does
   *  nothing". */
  next_cursor?: string | null;
}

type Verdict = "live" | "gone" | "unknown";

interface PreflightResponse {
  items: { id: string; status: Verdict; source?: "db" | "portal" }[];
  live: number;
  gone: number;
  unknown: number;
}

interface BulkJob {
  hot_offer_id: string;
  agent_user_id: string;
  agent_name: string | null;
  job_id: string;
  job_run_id: string;
  use_ss: boolean;
}

// Per-call cap accepted by /api/stats/competition/{id}/owner-finder.
// 25 keeps each round-trip snappy and lets results stream into the
// list in fine-grained chunks — the user sees matches appear roughly
// every half-second instead of waiting for a 200-row block at a time.
const PAGE_LIMIT = 25;

// Max cadence (ms) at which streamed scan pages are committed to React
// state. Pages arrive every few hundred ms; batching the state writes
// to this interval keeps a long sweep from re-rendering the list on
// every page while still feeling live.
const FLUSH_MS = 200;

// Once the match list grows past this, switch from a plain ``.map`` (one
// DOM node per row) to the windowed renderer below — see
// ``VirtualMatchList``. Smaller lists keep the richer rendering (section
// dividers, exact heights) since there's no perf reason to window them.
const VIRTUALIZE_ABOVE = 150;
// Fixed per-row stride in px used by the windower: MatchRow is ~66px
// (h-12 thumb + py-2) and the wrapper adds a 6px bottom gap. Must match
// the wrapper height in VirtualMatchList or scrolling will drift.
const ROW_STRIDE = 72;

/** "Scan latest N" presets — null means "drain every publishable row",
 *  "custom" means the user types a number into the adjacent input.
 *  Ordered from cheapest to most thorough so the select renders that
 *  way without a separate sort. */
type ScanCapPreset = number | null | "custom";
const SCAN_CAP_OPTIONS: { value: ScanCapPreset; label: string }[] = [
  { value: 100, label: "Latest 100" },
  { value: 250, label: "Latest 250" },
  { value: 500, label: "Latest 500" },
  { value: 1000, label: "Latest 1000" },
  { value: 5000, label: "Latest 5,000" },
  { value: 10000, label: "Latest 10,000" },
  { value: 15000, label: "Latest 15,000" },
  { value: "custom", label: "Custom…" },
  { value: null, label: "All listings" },
];
/** Match methods that identify the property outright rather than by
 *  resemblance — rendered in green. Covers both scans' vocabularies. */
const CERTAIN_METHODS = new Set(["cadastral", "self", "dedup"]);

/** Compact badge labels. Anything unlisted falls through to the raw value. */
const METHOD_LABELS: Record<string, string> = {
  cadastral: "cadastral",
  fingerprint_tight: "tight",
  fingerprint: "unique",
  self: "owner",
};

const DEFAULT_SCAN_CAP: number | null = 250;
const CUSTOM_SCAN_CAP_MAX = 100000;

// Listings per liveness call during "Verify & publish". The parsers never
// re-fetch a listing after ingest, so anything older than an hour is asked
// of the portal itself — ~1s per ss.ge row, ~2s per MyHome row through a
// headless browser. Ten per call keeps each round-trip to a few seconds so
// the verified counter visibly advances.
const PREFLIGHT_CHUNK = 10;
// How many extra scan pages the verify step may walk past the user's scan
// window to replace listings that turned out deleted. Bounded so "publish
// 25" against a filter with no live owner inventory left can't sweep the
// whole market.
const REPLACEMENT_SCAN_MAX_PAGES = 40;
// Default for the "Select top N" quick-pick — the typical bulk size.
const DEFAULT_TOP_N = 25;

// Scan source — same modal renders both the per-agency drill-in (used
// from /competition/{id}) and the market-wide scan (used from the bulk
// publish page). Discriminated so the fetch URL, request body, header
// copy, and the visibility of the count/time scan-mode toggle adapt
// without two near-identical 1k-line clones.
export type OwnerFinderSource =
  | {
      kind: "agency";
      agencyId: string;
      agencyName: string;
    }
  | {
      kind: "market";
      /** Seeds the in-modal ``FilterBar`` so it opens pre-populated with
       *  whatever the parent page had selected — the user can then refine
       *  the property filters right here and re-scan. The modal owns these
       *  from open onward; the parent's copy isn't read again. */
      initialFilters: Filters;
      /** Non-FilterBar request extras that should always ride along but
       *  aren't expressed by the FilterBar UI — e.g. ``market_agency_id``
       *  (scope to one firm), ``listed_since``/``listed_until`` (freshness
       *  window), ``text_query``, ``welcomes_agents``, the ``exclude_*``
       *  publishability guards, and ``sort``. Must NOT contain any field
       *  that ``filtersToBody`` emits, or clearing that filter in the
       *  modal wouldn't actually drop it from the scan. Merged under the
       *  modal's live ``filtersToBody(localFilters)`` + ``limit``/``offset``
       *  before hitting ``/api/market/owner-finder``. */
      baseBody: Record<string, unknown>;
      /** Short label shown in the header instead of an agency name —
       *  e.g. "Filtered market · 7d · Saburtalo". */
      label: string;
      /** Show a per-match agency tag (logo + name) on each result row. Set
       *  by the dashboard multi-agency scan so the user can tell which
       *  selected competitor each match came from. */
      showAgencyTags?: boolean;
    };

export function BulkOwnerFinderModal({
  source,
  onClose,
}: {
  source: OwnerFinderSource;
  onClose: () => void;
}) {
  const [items, setItems] = useState<FinderItem[] | null>(null);
  const [meta, setMeta] = useState<{
    total: number;
    processed: number;
    matched: number;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // True while the auto-sweep is mid-flight. Used to render the
  // "Scanning X of Y…" hint and the spinner-style striped border.
  const [scanning, setScanning] = useState(false);

  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);

  const [selectedListings, setSelectedListings] = useState<Set<string>>(
    new Set(),
  );
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  const [useSs, setUseSs] = useState(true);
  const [mhPayBalance, setMhPayBalance] = useState(true);

  // In-modal property filters (market source only). Seeded from the
  // parent page's current filter set so the modal opens showing what the
  // user already had, then becomes the single source of truth for the
  // scan's property constraints — editing a dropdown here narrows the
  // set that gets scanned on the next Start. Unused for the (legacy)
  // agency source, which has no FilterBar.
  const [localFilters, setLocalFilters] = useState<Filters>(
    source.kind === "market" ? source.initialFilters : {},
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Outcome line after a publish that skipped or replaced deleted rows —
  // the modal stays open in that case so the user can see what happened.
  const [result, setResult] = useState<string | null>(null);

  // Portal liveness per owner-listing id, filled by the verify step before
  // publishing (and by the server's own refusals). ``gone`` rows render
  // greyed with a Deleted badge and can't be selected again.
  const [verdicts, setVerdicts] = useState<Map<string, Verdict>>(new Map());
  const [verifyState, setVerifyState] = useState<{
    checked: number;
    dead: number;
    replaced: number;
    /** "unknown" verdicts swapped for a verified replacement. */
    unverified: number;
    target: number;
  } | null>(null);
  // "Select top N" quick-pick size.
  const [topN, setTopN] = useState<number>(DEFAULT_TOP_N);

  // Collapsible chrome — the property filters and the team picker can eat
  // most of the modal's height, leaving the match list a cramped sliver.
  // Toggling these closed hands that vertical space back to the results.
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [agentsOpen, setAgentsOpen] = useState(true);

  // Per-match agency tag — only for the dashboard multi-agency scan, where
  // the user needs to tell which selected competitor each match came from.
  const showAgency = source.kind === "market" && !!source.showAgencyTags;

  // ── scan-window controls ─────────────────────────────────────────
  // The two constraints are mutually exclusive: either bound by count
  // (newest N listings, any age) or by time window (every listing in
  // a date range, no cap). Both at once gets confusing for the user
  // and adds zero capability — the time window is already ordered by
  // newest, so "latest 100 in 7d" is just "in 7d, server sends them
  // newest-first." The modal opens in a pre-scan state and waits for
  // the user to commit a choice with the Start button.
  type ScanMode = "count" | "time";
  // Market source already encodes ``listed_since`` (the parent page has
  // its own freshness pill), so the "By time" toggle would be redundant
  // there. Lock scan mode to count for market sources; the toggle is
  // hidden below and the user controls time on the underlying page.
  const [scanMode, setScanMode] = useState<ScanMode>("count");
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ mode: "7d" });
  const [scanCap, setScanCap] = useState<number | null>(DEFAULT_SCAN_CAP);
  // True when the user picked the "Custom…" preset. Holds its own
  // separate ``customCap`` so flipping back to a preset and back to
  // custom keeps the typed-in number instead of resetting it to 0.
  const [useCustomCap, setUseCustomCap] = useState(false);
  const [customCap, setCustomCap] = useState<number>(2000);
  // Generation counter — every Start click bumps it and the effect
  // below re-runs. Reset = clear bumps + clear rows; the user has to
  // click Start again to fetch.
  const [scanGen, setScanGen] = useState(0);
  // Per-scan AbortController so the Stop button can cancel the
  // in-flight page request immediately instead of waiting for it to
  // resolve. Re-created at the top of each run().
  const abortRef = useRef<AbortController | null>(null);
  // Where the scan has got to. Lives in a ref (not state) because two
  // loops advance it across awaits: the scan effect below, and the verify
  // step's replacement search, which walks further pages with the same
  // filters when every already-scanned match has been used up.
  const scanCursorRef = useRef<{
    offset: number;
    cursor: string | null;
    scannedTotal: number;
    exhausted: boolean;
  }>({ offset: 0, cursor: null, scannedTotal: 0, exhausted: false });
  // The verify loop's live view of the de-duplicated list — the memo it
  // would otherwise read is frozen at the render that started the loop.
  const displayItemsRef = useRef<FinderItem[]>([]);

  // ── scan-result flush buffers ────────────────────────────────────
  // A big scan sweeps hundreds of 25-row pages. Committing each page
  // straight to state re-renders (and re-sorts) the whole growing list
  // every ~half-second — on a 10k-row scan that's ~400 full reconciles
  // and the tab locks up. Instead we accumulate arriving rows / newly
  // selected ids / the latest meta into refs and flush them to state on
  // a throttled cadence (and once more when the sweep ends), collapsing
  // those hundreds of renders into a handful. Reset at the top of each
  // run() below.
  const itemsBufferRef = useRef<FinderItem[]>([]);
  const selectBufferRef = useRef<Set<string>>(new Set());
  const metaBufferRef = useRef<{ total: number; processed: number } | null>(
    null,
  );
  const lastFlushRef = useRef(0);

  // Scroll container for the match list — handed to the windowed
  // renderer (VirtualMatchList) so it can read scrollTop/height and
  // mount only the rows in view on big result sets.
  const listScrollRef = useRef<HTMLDivElement>(null);

  const { registerBatch } = usePublishProgress();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Derive the request body for the active scan mode. Count mode
  // skips the time filter so the server returns "newest N first
  // regardless of age"; time mode skips the cap so we drain every
  // matching row in the window. Mixing both at once is intentionally
  // not allowed in the UI.
  const scanRequest = useMemo<{
    cap: number | null;
    body: { listed_since?: string; listed_until?: string };
  }>(() => {
    if (scanMode === "time") {
      return { cap: null, body: resolveTimeRange(timeRange) };
    }
    const effectiveCap = useCustomCap
      ? Math.max(1, Math.min(CUSTOM_SCAN_CAP_MAX, customCap || 1))
      : scanCap;
    return { cap: effectiveCap, body: {} };
  }, [scanMode, timeRange, scanCap, useCustomCap, customCap]);

  /** One page of the owner scan, advancing ``scanCursorRef``. Returns null
   *  once the filtered inventory is exhausted. URL + body shape differ
   *  between sources: agency mode hits the per-agency stats endpoint with
   *  optional time-window overrides — it still reads the frozen
   *  ``hot_offers`` snapshot, along with the rest of the Competition
   *  analytics pages. Market mode — which is only ever the Bulk publish
   *  page — hits the **live** scan over ssparser/mhparser with the
   *  in-modal filters: the page-specific extras (``baseBody``) underneath,
   *  then the FilterBar selection on top, so editing a filter here actually
   *  changes the scanned set. Shared by the scan effect and by the verify
   *  step, which scans deeper with the very same filters when it needs a
   *  replacement for a deleted listing. */
  const fetchScanPage = useCallback(
    async (
      pageLimit: number,
      signal: AbortSignal,
    ): Promise<FinderResponse | null> => {
      const cur = scanCursorRef.current;
      if (cur.exhausted) return null;
      const url =
        source.kind === "agency"
          ? `/api/stats/competition/${source.agencyId}/owner-finder`
          : "/api/market/live/owner-finder";
      let body: Record<string, unknown>;
      if (source.kind === "agency") {
        body = { limit: pageLimit, offset: cur.offset, ...scanRequest.body };
      } else {
        body = {
          ...source.baseBody,
          ...filtersToBody(localFilters),
          limit: pageLimit,
          offset: cur.offset,
          // Server prefers the cursor and ignores the offset when one is
          // present; the offset stays as the fallback for sorts that can't
          // be paged by keyset.
          cursor: cur.cursor,
        };
        // FilterBar's own sort wins when set — filtersToBody omits it, so
        // carry it across explicitly; otherwise baseBody's sort (if any)
        // stands.
        if (localFilters.sort) body.sort = localFilters.sort;
      }
      const r = await api<FinderResponse>(url, {
        method: "POST",
        json: body,
        signal,
      });
      cur.offset += r.processed;
      cur.cursor = r.next_cursor ?? null;
      cur.scannedTotal += r.processed;
      // A short page is the authoritative "that's everything" signal. The
      // total is only a stop condition when the server counted exactly —
      // when it's capped, there's more inventory past it.
      if (
        r.processed < pageLimit ||
        (r.total_exact !== false &&
          !r.total_capped &&
          cur.scannedTotal >= r.total)
      ) {
        cur.exhausted = true;
      }
      return r;
    },
    [source, scanRequest, localFilters],
  );

  // Pre-scan state vs. "scan in progress / done" state is keyed on
  // ``scanGen`` — every Start click bumps it; the effect below
  // re-runs end-to-end. ``scanGen === 0`` means the modal hasn't been
  // started yet, so we skip the fetch entirely.
  useEffect(() => {
    if (scanGen === 0) return;
    let cancelled = false;
    setItems([]);
    setMeta(null);
    setLoadError(null);
    setSelectedListings(new Set());
    setVerdicts(new Map());
    setVerifyState(null);
    setResult(null);
    setScanning(true);

    // Fresh buffers for this run — see the ref declarations above.
    itemsBufferRef.current = [];
    selectBufferRef.current = new Set();
    metaBufferRef.current = null;
    lastFlushRef.current = 0;

    // Drain the buffers into React state in one batched update. Cheap
    // enough to call on every page (it no-ops when nothing is pending),
    // but we gate the calls on a time throttle in the loop so renders
    // stay rare even when pages return fast.
    const flush = () => {
      if (cancelled) return;
      if (itemsBufferRef.current.length) {
        const buf = itemsBufferRef.current;
        itemsBufferRef.current = [];
        setItems((prev) => (prev ? prev.concat(buf) : buf.slice()));
      }
      if (selectBufferRef.current.size) {
        const add = selectBufferRef.current;
        selectBufferRef.current = new Set();
        setSelectedListings((prev) => {
          const next = new Set(prev);
          for (const id of add) next.add(id);
          return next;
        });
      }
      if (metaBufferRef.current) {
        const m = metaBufferRef.current;
        setMeta({ total: m.total, processed: m.processed, matched: 0 });
      }
    };

    // Sweep pages sequentially. We don't fan-out in parallel — the
    // server endpoint isn't cheap (one SQL fan-out per row in the
    // batch), and stacking calls would just queue them in the same
    // pool while doubling perceived latency. Each batch finishes in
    // sub-second; the user sees rows + counts grow live.
    const controller = new AbortController();
    abortRef.current = controller;
    const run = async () => {
      scanCursorRef.current = {
        offset: 0,
        cursor: null,
        scannedTotal: 0,
        exhausted: false,
      };
      const { cap } = scanRequest;
      try {
        while (!cancelled && !controller.signal.aborted) {
          const remaining =
            cap == null ? PAGE_LIMIT : cap - scanCursorRef.current.scannedTotal;
          if (remaining <= 0) break;
          const pageLimit = Math.min(PAGE_LIMIT, remaining);
          const r = await fetchScanPage(pageLimit, controller.signal);
          if (r === null) break;
          if (cancelled || controller.signal.aborted) return;
          // Buffer the freshly-arrived rows + pre-select every unplaced
          // match in the same pass so the checkbox state and the visible
          // list stay in sync. These go to refs, not state — ``flush``
          // commits them on the throttle below.
          for (const it of r.items) {
            itemsBufferRef.current.push(it);
            if (it.top_candidate && !it.top_candidate.listing.placement_id) {
              selectBufferRef.current.add(it.top_candidate.listing.id);
            }
          }
          metaBufferRef.current = {
            total: r.total,
            processed: scanCursorRef.current.scannedTotal,
          };
          // Throttle: only push to React state every FLUSH_MS so a fast
          // run doesn't re-render (and re-sort) the whole list per page.
          const now = performance.now();
          if (now - lastFlushRef.current >= FLUSH_MS) {
            lastFlushRef.current = now;
            flush();
          }
          if (scanCursorRef.current.exhausted) break;
          if (cap != null && scanCursorRef.current.scannedTotal >= cap) break;
        }
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;
        // ``fetch`` raises a DOMException with name "AbortError" when
        // the signal fires — that's the Stop button, not a real
        // failure, so swallow it instead of showing a red banner.
        if (e instanceof DOMException && e.name === "AbortError") return;
        setLoadError(
          e instanceof ApiError ? e.message : "Failed to load owner matches",
        );
      } finally {
        // Commit whatever's still buffered — the sweep finished, was
        // stopped, or errored; either way the user should see every row
        // we did manage to fetch. ``flush`` no-ops if cancelled (unmount).
        flush();
        if (!cancelled) setScanning(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    };
    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanGen]);

  /** Stop the in-flight scan. Keeps everything matched so far visible
   *  so the user can publish what they've got — the alternative would
   *  be a wasted scan. */
  function stopScan() {
    abortRef.current?.abort();
    setScanning(false);
  }

  // Reset everything when the user changes the constraint after a
  // finished scan — they have to click Start again to refetch.
  function resetScan() {
    setItems([]);
    setMeta(null);
    setLoadError(null);
    setSelectedListings(new Set());
    setVerdicts(new Map());
    setVerifyState(null);
    setResult(null);
    setScanning(false);
    setScanGen(0);
  }

  // Render order: actionable matches up top, sorted by confidence desc
  // (dedup → 95, best heuristic next); already-uploaded matches next
  // (same sort, but pushed below so the user's eye lands on rows that
  // still need work); unmatched rows last in arrival order. The matched
  // section is also de-duplicated on the owner-candidate's ``listing.id``
  // — multiple competitor listings often dedup-graph to the same
  // private-owner row, and queueing the same owner-listing twice would
  // publish the same property twice. Sorting first means the highest-
  // scored occurrence is the one we keep.
  const displayItems = useMemo(() => {
    const list = items ?? [];
    const matched = list.filter((it) => it.top_candidate);
    const unmatched = list.filter((it) => !it.top_candidate);
    matched.sort((a, b) => {
      const ap = a.top_candidate!.listing.placement_id ? 1 : 0;
      const bp = b.top_candidate!.listing.placement_id ? 1 : 0;
      if (ap !== bp) return ap - bp;
      return (
        (b.top_candidate?.score ?? 0) - (a.top_candidate?.score ?? 0)
      );
    });
    const seen = new Set<string>();
    const dedupedMatched: FinderItem[] = [];
    for (const it of matched) {
      const cid = it.top_candidate!.listing.id;
      if (seen.has(cid)) continue;
      seen.add(cid);
      dedupedMatched.push(it);
    }
    return [...dedupedMatched, ...unmatched];
  }, [items]);

  useEffect(() => {
    displayItemsRef.current = displayItems;
  }, [displayItems]);

  // Live ``matched`` counter, derived from the de-duped view so it
  // matches the number of rows the user actually sees. Counting raw
  // ``items`` would over-report (e.g. "30 matched" when only 18 unique
  // owner listings are visible). ``alreadyPlacedCount`` is the subset
  // of those matches that we've already uploaded ourselves — surfaced
  // separately so the user can see at-a-glance how much of the result
  // set is actionable vs. duplicate work.
  const { matchedCount, alreadyPlacedCount } = useMemo(() => {
    let matched = 0;
    let placed = 0;
    for (const it of displayItems) {
      if (!it.top_candidate) continue;
      matched += 1;
      if (it.top_candidate.listing.placement_id) placed += 1;
    }
    return { matchedCount: matched, alreadyPlacedCount: placed };
  }, [displayItems]);

  useEffect(() => {
    let cancelled = false;
    api<TeamMember[]>("/api/team")
      .then((r) => {
        if (cancelled) return;
        // Owners don't publish inventory themselves — they're excluded from
        // the bulk owner-scan assignment pool. Every other active role stays.
        setTeam(r.filter((m) => m.status === "active" && m.role !== "executive_manager"));
      })
      .catch((e) => {
        if (cancelled) return;
        setTeamError(
          e instanceof Error ? e.message : "Failed to load team",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedTeam = useMemo(() => {
    if (!team) return null;
    return [...team].sort(
      (a, b) =>
        (a.placements_today ?? 0) - (b.placements_today ?? 0) ||
        (a.full_name || a.email).localeCompare(b.full_name || b.email),
    );
  }, [team]);

  const orderedAgents = useMemo(() => {
    if (!sortedTeam) return [] as string[];
    return sortedTeam
      .filter((m) => selectedAgents.has(m.id))
      .map((m) => m.id);
  }, [sortedTeam, selectedAgents]);

  // Round-robin preview — purely cosmetic; real assignment happens
  // server-side and follows the same modulo. Iterate ``displayItems``
  // (matches sorted-by-score) so the agent assigned to slot 0 gets the
  // strongest match, slot 1 the next, etc.
  const orderedSelectedListings = useMemo(() => {
    const out: string[] = [];
    for (const it of displayItems) {
      const cid = it.top_candidate?.listing.id;
      if (cid && selectedListings.has(cid)) out.push(cid);
    }
    return out;
  }, [displayItems, selectedListings]);

  const assignmentPreview = useMemo(() => {
    if (orderedAgents.length === 0) return new Map<string, number>();
    const counts = new Map<string, number>();
    orderedSelectedListings.forEach((_, idx) => {
      const aid = orderedAgents[idx % orderedAgents.length];
      counts.set(aid, (counts.get(aid) ?? 0) + 1);
    });
    return counts;
  }, [orderedSelectedListings, orderedAgents]);

  const agentById = useMemo(() => {
    const m = new Map<string, TeamMember>();
    (team ?? []).forEach((t) => m.set(t.id, t));
    return m;
  }, [team]);

  const uncredentialled = useMemo(() => {
    if (!team) return [] as TeamMember[];
    return team.filter(
      (m) =>
        selectedAgents.has(m.id) &&
        (!m.has_myhome_creds || !m.has_myhome_phone),
    );
  }, [team, selectedAgents]);

  const canSubmit =
    orderedSelectedListings.length > 0 &&
    selectedAgents.size > 0 &&
    uncredentialled.length === 0 &&
    !submitting;

  function toggleListing(id: string) {
    setSelectedListings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAgent(id: string) {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAllListings() {
    const next = new Set<string>();
    for (const it of displayItems) {
      if (it.top_candidate && !it.top_candidate.listing.placement_id) {
        if (verdicts.get(it.top_candidate.listing.id) === "gone") continue;
        next.add(it.top_candidate.listing.id);
      }
    }
    setSelectedListings(next);
  }
  /** Pick the N strongest unplaced matches (display order is score-desc),
   *  skipping anything already verified as deleted. This is "I want 25
   *  placements" — the verify step then keeps that count by replacing any
   *  that turn out gone. */
  function selectTopN() {
    const n = Math.max(1, Math.floor(topN || 1));
    const next = new Set<string>();
    for (const it of displayItems) {
      if (next.size >= n) break;
      const cand = it.top_candidate?.listing;
      if (!cand || cand.placement_id) continue;
      if (verdicts.get(cand.id) === "gone") continue;
      next.add(cand.id);
    }
    setSelectedListings(next);
  }
  function clearListings() {
    setSelectedListings(new Set());
  }
  // Add every already-uploaded match (owner listing we've previously placed)
  // to the current selection — additive so it composes with "Select all".
  // Useful for re-publishing / re-assigning inventory the agency already owns.
  function selectAlreadyUploaded() {
    setSelectedListings((prev) => {
      const next = new Set(prev);
      for (const it of displayItems) {
        if (it.top_candidate && it.top_candidate.listing.placement_id) {
          next.add(it.top_candidate.listing.id);
        }
      }
      return next;
    });
  }
  // Count of already-uploaded matches in view — drives the button's enabled
  // state + its label so the user knows there's something to select.
  const uploadedInView = useMemo(
    () =>
      displayItems.filter(
        (it) => it.top_candidate && it.top_candidate.listing.placement_id,
      ).length,
    [displayItems],
  );
  function selectAllAgents() {
    if (!team) return;
    setSelectedAgents(new Set(team.map((m) => m.id)));
  }
  function clearAgents() {
    setSelectedAgents(new Set());
  }
  function autoPickAgents() {
    if (!sortedTeam) return;
    const credentialled = sortedTeam.filter(
      (m) => m.has_myhome_creds && m.has_myhome_phone,
    );
    if (credentialled.length === 0) return;
    setSelectedAgents(new Set(credentialled.map((m) => m.id)));
  }

  /** The next unused match to stand in for one that turned out deleted.
   *  Draws from the already-scanned list first (score order, so the best
   *  reserve match goes first), then keeps scanning deeper with the same
   *  filters — the user asked for N placements, not for N minus whatever
   *  the portals took down since the scan. */
  async function takeReplacement(
    used: Set<string>,
    dead: Set<string>,
    signal: AbortSignal,
  ): Promise<string | null> {
    const fromList = (list: FinderItem[]): string | null => {
      for (const it of list) {
        const cand = it.top_candidate?.listing;
        if (!cand) continue;
        if (cand.placement_id) continue;
        if (used.has(cand.id) || dead.has(cand.id)) continue;
        return cand.id;
      }
      return null;
    };
    const ready = fromList(displayItemsRef.current);
    if (ready) return ready;
    // Reserve empty — walk further into the filtered market. Agency mode
    // has no cursor to continue from (its endpoint pages by offset only),
    // but the same loop works since fetchScanPage tracks the offset.
    let pages = 0;
    while (
      !scanCursorRef.current.exhausted &&
      !signal.aborted &&
      pages < REPLACEMENT_SCAN_MAX_PAGES
    ) {
      pages += 1;
      const r = await fetchScanPage(PAGE_LIMIT, signal);
      if (r === null) break;
      setItems((prev) => (prev ? prev.concat(r.items) : r.items.slice()));
      setMeta({
        total: r.total,
        processed: scanCursorRef.current.scannedTotal,
        matched: 0,
      });
      // The ``items`` state update above won't have re-rendered (and
      // refreshed the ref) before the next iteration reads it, so keep
      // the loop's own view current by hand.
      displayItemsRef.current = displayItemsRef.current.concat(r.items);
      const hit = fromList(r.items);
      if (hit) return hit;
    }
    return null;
  }

  async function submit() {
    if (!canSubmit || !items) return;
    const controller = new AbortController();
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);

    const wanted = orderedSelectedListings.length;
    // Agency mode reads the frozen hot_offers snapshot and publishes
    // hot_offers ids — the live preflight can't resolve those, so it
    // publishes as before. Market mode (the Bulk page) verifies first.
    const verify = source.kind === "market";
    const confirmed: string[] = [];
    const dead = new Set<string>();
    const used = new Set<string>(orderedSelectedListings);
    const queue = [...orderedSelectedListings];
    let replaced = 0;
    let unverified = 0;
    let checked = 0;
    if (verify) setVerifyState({ checked: 0, dead: 0, replaced: 0, unverified: 0, target: wanted });

    try {
      if (!verify) {
        confirmed.push(...queue);
      }
      while (verify && queue.length && confirmed.length < wanted) {
        const chunk = queue.splice(0, PREFLIGHT_CHUNK);
        const r = await api<PreflightResponse>("/api/market/live/preflight", {
          method: "POST",
          json: { listing_ids: chunk },
          signal: controller.signal,
        });
        const status = new Map(r.items.map((i) => [i.id, i.status]));
        setVerdicts((prev) => {
          const next = new Map(prev);
          for (const [id, v] of status) next.set(id, v);
          return next;
        });
        for (const id of chunk) {
          checked += 1;
          if (status.get(id) !== "gone") {
            if (status.get(id) === "live") {
              confirmed.push(id);
              continue;
            }
            // "unknown" means the portal didn't answer definitively (a half-served
            // page, a throttle, a timeout). Two such rows went to publish on
            // 2026-10-17 and failed as deleted. Swap the row for a verified
            // replacement when there is one; keep it only when there is
            // nothing else, so a probe outage can't empty the batch.
            const rep = await takeReplacement(used, dead, controller.signal);
            if (!rep) {
              confirmed.push(id);
              continue;
            }
            unverified += 1;
            used.add(rep);
            queue.push(rep);
            setSelectedListings((prev) => {
              const next = new Set(prev);
              next.delete(id);
              next.add(rep);
              return next;
            });
            setVerifyState({ checked, dead: dead.size, replaced, unverified, target: wanted });
            continue;
          }
          dead.add(id);
          setSelectedListings((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          const rep = await takeReplacement(used, dead, controller.signal);
          if (rep) {
            used.add(rep);
            queue.push(rep);
            replaced += 1;
            setSelectedListings((prev) => new Set(prev).add(rep));
          }
          setVerifyState({ checked, dead: dead.size, replaced, unverified, target: wanted });
        }
        setVerifyState({ checked, dead: dead.size, replaced, unverified, target: wanted });
      }

      if (confirmed.length === 0) {
        setSubmitError(
          "Every selected listing has been taken down on the portal, and there was nothing left to replace them with. Widen the filters or scan deeper.",
        );
        return;
      }

      // Publish against whichever catalogue the scan read, or the ids
      // won't resolve: the live scan returns ``market_listings`` ids, the
      // frozen agency scan returns ``hot_offers`` ids. Both are UUIDs, so
      // crossing them fails as "offer_not_found" rather than loudly.
      const publishUrl =
        source.kind === "agency"
          ? "/api/market/bulk-publish"
          : "/api/market/live/bulk-publish";
      const r = await api<{
        jobs: BulkJob[];
        skipped_offer_ids: string[];
        gone_offer_ids?: string[];
        agent_offer_ids?: string[];
        no_photo_offer_ids?: string[];
        batch_id: string;
      }>(publishUrl, {
        method: "POST",
        json: {
          hot_offer_ids: confirmed,
          agent_user_ids: orderedAgents,
          mh_pay_balance: mhPayBalance,
          use_ss: useSs,
        },
      });
      const skipped = r.skipped_offer_ids?.length ?? 0;
      // The server re-checks its own records at publish time and drops rows
      // it knows are gone; fold those into the same "deleted" tally.
      const serverGone = r.gone_offer_ids ?? [];
      for (const id of serverGone) dead.add(id);
      if (serverGone.length) {
        setVerdicts((prev) => {
          const next = new Map(prev);
          for (const id of serverGone) next.set(id, "gone");
          return next;
        });
      }
      // "Owner" rows the server unmasked as agencies at publish time. The
      // scan already excludes them, so this only catches a verdict that
      // changed between scan and publish.
      const agents = r.agent_offer_ids?.length ?? 0;
      const noPhoto = r.no_photo_offer_ids?.length ?? 0;
      // Every selected listing was already published by all the chosen
      // agents — nothing spawned. Keep the modal open and explain rather
      // than silently closing on a no-op.
      if (r.jobs.length === 0) {
        setSubmitError(
          skipped > 0
            ? `All ${skipped} selected listing${skipped === 1 ? "" : "s"} are already published by the chosen agent${selectedAgents.size === 1 ? "" : "s"} — nothing to publish. Pick a different agent or different listings.`
            : agents > 0 && dead.size === 0
              ? `All ${agents} selected listing${agents === 1 ? "" : "s"} turned out to be posted by agencies registered as private sellers — nothing to publish.`
              : noPhoto > 0 && dead.size === 0 && agents === 0
                ? `All ${noPhoto} selected listing${noPhoto === 1 ? "" : "s"} have no photos, which MyHome requires — nothing to publish.`
                : dead.size > 0
                  ? "Every selected listing has been taken down on the portal or was posted by an agency — nothing to publish."
                  : "Nothing was published.",
        );
        return;
      }
      // One aggregate for the whole batch instead of one register() per
      // listing — see the same note in MarketPublish's submit().
      registerBatch({ batchId: r.batch_id, total: r.jobs.length, useSs });
      if (dead.size === 0 && skipped === 0 && agents === 0 && unverified === 0 && noPhoto === 0) {
        onClose();
        return;
      }
      // Something was skipped or swapped — stay open so the user can see
      // what the batch actually contains. Published rows leave the
      // selection so a second click can't double-publish them.
      setResult(
        `Publishing ${r.jobs.length} listing${r.jobs.length === 1 ? "" : "s"} across ${orderedAgents.length} agent${orderedAgents.length === 1 ? "" : "s"}` +
          (dead.size
            ? ` · skipped ${dead.size} deleted listing${dead.size === 1 ? "" : "s"}${replaced ? `, replaced ${replaced}` : ""}`
            : "") +
          (unverified
            ? ` · swapped ${unverified} unverified listing${unverified === 1 ? "" : "s"}`
            : "") +
          (agents
            ? ` · skipped ${agents} agency listing${agents === 1 ? "" : "s"} (registered as private sellers)`
            : "") +
          (noPhoto
            ? ` · skipped ${noPhoto} listing${noPhoto === 1 ? "" : "s"} without photos`
            : "") +
          (skipped ? ` · ${skipped} already published` : ""),
      );
      setSelectedListings((prev) => {
        const next = new Set(prev);
        for (const id of confirmed) next.delete(id);
        return next;
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Bulk publish failed — try again",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-stretch sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-5xl sm:max-h-[92vh] sm:rounded-2xl bg-ink-950 border border-white/10 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── header ── */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-white/5">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500">
              {source.kind === "agency"
                ? "Bulk-publish their owners"
                : "Scan for owners (bulk)"}
            </div>
            <div className="text-base font-semibold text-slate-100 truncate">
              {source.kind === "agency" ? source.agencyName : source.label}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {source.kind === "agency" ? (
                <>
                  For every active listing this competitor posts, we look for
                  the same property listed by its actual owner (dedup graph +
                  tight attribute heuristic) and queue the owner version for
                  publish.
                </>
              ) : (
                <>
                  Walks the filtered live market and, for each row, surfaces
                  the owner-direct version — matched on the cadastral code
                  where both listings carry one, otherwise on a tight
                  neighborhood/rooms/floor/area/price fingerprint. Agency
                  reposts are replaced by the owner behind them; rows that
                  are already owner-direct are kept as &quot;owner&quot;.
                  More reliable than the &quot;Hide agency reposts&quot;
                  toggle, which only sees the portal&apos;s own label and
                  misses agents posting under an unset one.
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            className="text-slate-500 hover:text-slate-200 p-1 -m-1"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ── property filters (market source) ──
            Same FilterBar used on Market / Bulk-publish, now in-modal so
            the user can narrow the property set right before scanning.
            Editing any filter discards a finished scan (resetScan) so the
            results never lag the constraints — they re-run Start. */}
        {source.kind === "market" && (
          <div className="px-3 py-2 border-b border-white/5 bg-ink-950/60">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition"
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform ${filtersOpen ? "rotate-90" : ""}`}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Property filters · narrow the set, then scan
              {!filtersOpen && hasAnyFilter(localFilters) && (
                <span className="text-accent-300 normal-case tracking-normal">
                  · active
                </span>
              )}
            </button>
            {filtersOpen && (
              <div className="mt-2">
                <FilterBar
                  filters={localFilters}
                  onChange={(next) => {
                    setLocalFilters(next);
                    if (scanGen > 0) resetScan();
                  }}
                  hideOwnerDirect
                />
              </div>
            )}
          </div>
        )}

        {/* ── action bar ── */}
        <div className="p-3 border-b border-white/5 bg-ink-950 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={autoPickAgents}
              disabled={(team?.length ?? 0) === 0}
              title="Auto-pick every credentialled active agent in load-ascending order"
            >
              ⚡ Auto-pick agents
            </button>

            {/* Scan-mode toggle. The two constraints are mutually
                exclusive — pick a count OR a time window, never both.
                Disabled while a scan is in flight so the user can't
                yank the rug mid-sweep. Hidden for market sources
                because the parent page already owns the time window
                (its freshness pill writes ``listed_since`` into
                ``source.filters``); a second time control on the modal
                would just conflict. */}
            {source.kind === "agency" && (
              <div className="inline-flex rounded-lg bg-ink-900/60 border border-white/5 p-0.5 text-[11px] font-medium">
                {(["count", "time"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={scanning}
                    onClick={() => {
                      if (m === scanMode) return;
                      setScanMode(m);
                      if (scanGen > 0) resetScan();
                    }}
                    className={`px-2.5 py-1 rounded-md transition-colors ${
                      scanMode === m
                        ? "bg-accent-500/15 text-accent-200"
                        : "text-slate-500 hover:text-slate-300"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {m === "count" ? "By count" : "By time"}
                  </button>
                ))}
              </div>
            )}

            {source.kind === "agency" && scanMode === "time" ? (
              <TimeRangeDropdown
                value={timeRange}
                onChange={(v) => {
                  setTimeRange(v);
                  if (scanGen > 0) resetScan();
                }}
              />
            ) : (
              <div className="inline-flex items-center gap-1.5">
                <select
                  className="input !py-1 !text-xs h-9 w-auto"
                  disabled={scanning}
                  value={
                    useCustomCap
                      ? "custom"
                      : scanCap == null
                        ? "all"
                        : String(scanCap)
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "custom") {
                      setUseCustomCap(true);
                    } else {
                      setUseCustomCap(false);
                      setScanCap(v === "all" ? null : Number(v));
                    }
                    if (scanGen > 0) resetScan();
                  }}
                  title="How many of the most-recent listings to scan"
                >
                  {SCAN_CAP_OPTIONS.map((o) => (
                    <option
                      key={o.label}
                      value={
                        o.value == null
                          ? "all"
                          : o.value === "custom"
                            ? "custom"
                            : o.value
                      }
                    >
                      {o.label}
                    </option>
                  ))}
                </select>
                {useCustomCap && (
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={CUSTOM_SCAN_CAP_MAX}
                    step={100}
                    disabled={scanning}
                    value={customCap}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setCustomCap(
                        Number.isFinite(n) && n > 0
                          ? Math.min(CUSTOM_SCAN_CAP_MAX, Math.floor(n))
                          : 0,
                      );
                      if (scanGen > 0) resetScan();
                    }}
                    placeholder="e.g. 7500"
                    className="input !py-1 !text-xs h-9 w-24 text-right tabular-nums"
                    title={`Number of newest listings to scan (max ${CUSTOM_SCAN_CAP_MAX.toLocaleString()})`}
                  />
                )}
              </div>
            )}

            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={scanning || (useCustomCap && customCap < 1)}
              onClick={() => setScanGen((g) => g + 1)}
              title={
                scanGen === 0
                  ? source.kind === "agency"
                    ? "Scan this competitor with the selected constraint"
                    : "Scan the filtered market for owner-direct matches"
                  : "Discard current results and re-scan with the selected constraint"
              }
            >
              {scanning
                ? "Scanning…"
                : scanGen === 0
                  ? "▶ Start scan"
                  : "↻ Re-scan"}
            </button>

            {scanning && (
              <button
                type="button"
                onClick={stopScan}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15 hover:border-rose-500/60 text-rose-200 text-sm px-3 h-9 transition"
                title="Stop the scan but keep what's matched so far — you can still publish the selected rows"
              >
                ■ Stop
              </button>
            )}

            {meta && (
              <ScanProgress
                scanning={scanning}
                matched={matchedCount}
                alreadyPlaced={alreadyPlacedCount}
                processed={meta.processed}
                // ``scope`` is what the user asked us to scan — the
                // cap in count mode, or the full total when there is
                // no cap (time mode, or count mode with "All"). The
                // progress bar fills against this, not against the
                // absolute agency total, otherwise "Latest 100 of
                // 8,671" would crawl across the bar at 1.2 % even
                // when fully done.
                scope={
                  scanMode === "count" && scanRequest.cap != null
                    ? Math.min(scanRequest.cap, meta.total)
                    : meta.total
                }
                total={meta.total}
              />
            )}

            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mhPayBalance}
                  onChange={(e) => setMhPayBalance(e.target.checked)}
                />
                MyHome balance
              </label>
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSs}
                  onChange={(e) => setUseSs(e.target.checked)}
                />
                Also SS.ge
              </label>
              {verifyState && (
                <span className="text-[11px] text-slate-400 tabular-nums">
                  verified {verifyState.checked}/{verifyState.target}
                  {verifyState.dead > 0 && (
                    <span className="text-rose-300">
                      {" "}
                      · {verifyState.dead} deleted
                    </span>
                  )}
                  {verifyState.replaced > 0 && (
                    <span className="text-emerald-300">
                      {" "}
                      · {verifyState.replaced} replaced
                    </span>
                  )}
                  {verifyState.unverified > 0 && (
                    <span className="text-amber-300">
                      {" "}
                      · {verifyState.unverified} unverified, swapped
                    </span>
                  )}
                </span>
              )}
              <button
                className="btn-primary text-sm"
                disabled={!canSubmit}
                onClick={submit}
                title={
                  !canSubmit
                    ? "Pick at least one owner listing and one agent"
                    : source.kind === "market"
                      ? `Check ${orderedSelectedListings.length} owner listings are still live on the portal (deleted ones are swapped for the next match), then spawn the jobs across ${selectedAgents.size} agent${selectedAgents.size === 1 ? "" : "s"}`
                      : `Spawn ${orderedSelectedListings.length} pipeline jobs across ${selectedAgents.size} agent${selectedAgents.size === 1 ? "" : "s"}`
                }
              >
                {submitting
                  ? verifyState && verifyState.checked < verifyState.target
                    ? "Verifying…"
                    : "Publishing…"
                  : orderedSelectedListings.length > 0 &&
                      selectedAgents.size > 0
                    ? `${source.kind === "market" ? "Verify & publish" : "Publish"} ${orderedSelectedListings.length} → ${selectedAgents.size}`
                    : "Publish"}
              </button>
            </div>
          </div>

          {orderedSelectedListings.length > 0 && selectedAgents.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-slate-400">Round-robin →</span>
              {orderedAgents.map((aid) => {
                const agent = agentById.get(aid);
                const count = assignmentPreview.get(aid) ?? 0;
                if (!agent) return null;
                return (
                  <span
                    key={aid}
                    className="inline-flex items-center gap-1.5 rounded-full border border-accent-500/40 bg-accent-500/10 px-2 py-0.5 text-[11px] text-accent-100"
                  >
                    <Avatar
                      url={agent.avatar_url}
                      name={agent.full_name}
                      email={agent.email}
                      size="xs"
                      online={agent.is_online}
                    />
                    <span className="truncate max-w-[90px]">
                      {agent.full_name?.split(" ")[0] || agent.email}
                    </span>
                    <span className="font-semibold">× {count}</span>
                  </span>
                );
              })}
            </div>
          )}

          {uncredentialled.length > 0 && (
            <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              ⛔{" "}
              {uncredentialled.length === 1
                ? "1 picked agent"
                : `${uncredentialled.length} picked agents`}{" "}
              can't be assigned —{" "}
              {uncredentialled
                .map((m) => m.full_name?.split(" ")[0] || m.email)
                .join(", ")}{" "}
              {uncredentialled.length === 1 ? "is" : "are"} missing MyHome
              creds.
            </div>
          )}

          {submitError && (
            <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              {submitError}
            </div>
          )}
          {result && (
            <div className="text-emerald-200 text-xs bg-emerald-500/10 border border-emerald-500/25 rounded p-2">
              ✓ {result}
            </div>
          )}
        </div>

        {/* ── agent picker ── */}
        <div className="px-3 pt-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setAgentsOpen((v) => !v)}
              className="label !mb-0 text-xs flex items-center gap-1.5 hover:text-slate-200 transition"
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform ${agentsOpen ? "rotate-90" : ""}`}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Team{" "}
              <span className="text-slate-600 font-normal">
                {agentsOpen
                  ? "· sorted by today's load (lightest first)"
                  : selectedAgents.size > 0
                    ? `· ${selectedAgents.size} selected`
                    : "· none selected"}
              </span>
            </button>
            {team && team.length > 0 && agentsOpen && (
              <div className="flex gap-1">
                <button
                  type="button"
                  className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
                  onClick={selectAllAgents}
                >
                  All
                </button>
                <button
                  type="button"
                  className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
                  onClick={clearAgents}
                  disabled={selectedAgents.size === 0}
                >
                  None
                </button>
              </div>
            )}
          </div>
          {!agentsOpen ? null : teamError ? (
            <div className="text-rose-300 text-xs">{teamError}</div>
          ) : sortedTeam === null ? (
            <Skeleton className="h-10" />
          ) : sortedTeam.length === 0 ? (
            <div className="text-xs text-slate-500">
              No active team members.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto -mr-2 pr-2">
              {sortedTeam.map((m) => {
                const on = selectedAgents.has(m.id);
                const credsOk = m.has_myhome_creds && m.has_myhome_phone;
                const today = m.placements_today ?? 0;
                const assigned = assignmentPreview.get(m.id) ?? 0;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleAgent(m.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                      !credsOk
                        ? on
                          ? "border-rose-500/50 bg-rose-500/10 text-rose-200"
                          : "border-rose-500/30 bg-rose-500/[0.04] text-rose-300/80 hover:border-rose-500/50"
                        : on
                          ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                          : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100"
                    }`}
                  >
                    <Avatar
                      url={m.avatar_url}
                      name={m.full_name}
                      email={m.email}
                      size="xs"
                      online={m.is_online}
                    />
                    <span className="truncate max-w-[130px]">
                      {m.full_name || m.email}
                    </span>
                    <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full bg-white/[0.06] text-slate-300">
                      {today}
                    </span>
                    {on && assigned > 0 && (
                      <span className="text-[10px] font-semibold text-accent-300">
                        ×{assigned}
                      </span>
                    )}
                    {!credsOk && (
                      <span
                        className="text-[10px] text-rose-300"
                        title="MyHome credentials missing"
                      >
                        ⛔
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── listings list ── */}
        <div className="flex-1 flex flex-col min-h-0 p-3">
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="label !mb-0 text-xs">
              Matches{" "}
              <span className="text-slate-600 font-normal">
                · their listing → owner's listing
              </span>
              {selectedListings.size > 0 && (
                <span className="ml-2 text-slate-300 font-normal">
                  · {selectedListings.size} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center rounded-md border border-white/10 bg-white/[0.03] overflow-hidden">
                <button
                  type="button"
                  className="text-[11px] hover:bg-white/[0.06] text-slate-300 hover:text-slate-100 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={selectTopN}
                  disabled={!items || items.length === 0}
                  title="Select the N strongest unplaced matches. Verify & publish keeps that count by swapping any that turn out deleted for the next match."
                >
                  Select top
                </button>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={topN}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setTopN(Number.isFinite(n) && n > 0 ? Math.min(500, Math.floor(n)) : 1);
                  }}
                  className="w-12 bg-transparent border-l border-white/10 text-[11px] text-slate-200 px-1.5 py-1 tabular-nums focus:outline-none"
                  aria-label="How many matches to select"
                />
              </span>
              <button
                type="button"
                className="text-[11px] rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-slate-300 hover:text-slate-100 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={selectAllListings}
                disabled={!items || items.length === 0}
                title="Select every unplaced match"
              >
                Select all
              </button>
              <button
                type="button"
                className="text-[11px] rounded-md border border-amber-500/30 bg-amber-500/[0.06] hover:bg-amber-500/[0.12] text-amber-200 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={selectAlreadyUploaded}
                disabled={uploadedInView === 0}
                title="Add every already-uploaded match to the selection"
              >
                Select uploaded{uploadedInView > 0 ? ` (${uploadedInView})` : ""}
              </button>
              <button
                type="button"
                className="text-[11px] rounded-md border border-white/10 bg-white/[0.03] hover:bg-rose-500/10 hover:border-rose-500/30 hover:text-rose-200 text-slate-300 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={clearListings}
                disabled={selectedListings.size === 0}
                title="Deselect every owner listing"
              >
                Clear selection
              </button>
            </div>
          </div>
          <div
            ref={listScrollRef}
            className="flex-1 min-h-0 overflow-auto"
          >
          {loadError ? (
            <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
              {loadError}
            </div>
          ) : scanGen === 0 ? (
            <div className="card p-8 text-center text-sm text-slate-500 border-dashed">
              <div className="text-slate-300 font-medium mb-1">
                Pick a constraint, then click ▶ Start scan
              </div>
              <div className="text-xs text-slate-500">
                {scanMode === "count" ? (
                  <>
                    Currently set to scan the{" "}
                    <span className="text-slate-300">
                      {scanRequest.cap == null
                        ? "every active"
                        : `latest ${scanRequest.cap.toLocaleString()}`}
                    </span>{" "}
                    listing
                    {scanRequest.cap === 1 ? "" : "s"}{" "}
                    {source.kind === "agency"
                      ? "this competitor has on the portal"
                      : "matching your Market filters"}{" "}
                    — newest first — and surface the owner-direct version of
                    each one.
                  </>
                ) : (
                  <>
                    Currently set to scan every listing posted in the
                    selected time window — newest first — and surface the
                    owner-direct version of each one.
                  </>
                )}
              </div>
            </div>
          ) : items === null || (items.length === 0 && scanning) ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : displayItems.length === 0 ? (
            <div className="card p-8 text-center text-sm text-slate-500">
              {source.kind === "agency"
                ? "No publishable listings from this competitor in this window."
                : "No publishable listings match the current Market filters."}
            </div>
          ) : displayItems.length > VIRTUALIZE_ABOVE ? (
            // Big result set — window the list so the DOM only ever
            // holds the rows in view (+overscan). The section dividers
            // are dropped here for uniform row heights; the matches-first
            // sort still carries the ordering intent.
            <VirtualMatchList
              items={displayItems}
              selectedListings={selectedListings}
              verdicts={verdicts}
              onToggle={toggleListing}
              showAgency={showAgency}
              scrollParentRef={listScrollRef}
            />
          ) : (
            <div className="space-y-1.5">
              {(() => {
                // Two boundaries get a divider so the "matches up top"
                // intent stays visually legible without forcing the
                // user to scan every row for a missing chip:
                //   1. actionable matches → already-uploaded matches
                //   2. all matches → unmatched (no owner found)
                const firstAlreadyPlaced = displayItems.findIndex(
                  (it) =>
                    !!it.top_candidate &&
                    !!it.top_candidate.listing.placement_id,
                );
                const firstUnmatched = displayItems.findIndex(
                  (it) => !it.top_candidate,
                );
                return displayItems.map((it, idx) => (
                  <div key={it.competitor_listing.id}>
                    {idx === firstAlreadyPlaced &&
                      firstAlreadyPlaced > 0 && (
                        <div className="flex items-center gap-2 my-3 px-1">
                          <div className="h-px flex-1 bg-amber-500/15" />
                          <span className="text-[10px] uppercase tracking-wider text-amber-300/80">
                            Already uploaded by your agency
                          </span>
                          <div className="h-px flex-1 bg-amber-500/15" />
                        </div>
                      )}
                    {idx === firstUnmatched && firstUnmatched > 0 && (
                      <div className="flex items-center gap-2 my-3 px-1">
                        <div className="h-px flex-1 bg-white/5" />
                        <span className="text-[10px] uppercase tracking-wider text-slate-600">
                          No owner match in DB
                        </span>
                        <div className="h-px flex-1 bg-white/5" />
                      </div>
                    )}
                    <MatchRow
                      item={it}
                      showAgency={showAgency}
                      selected={
                        !!it.top_candidate &&
                        selectedListings.has(it.top_candidate.listing.id)
                      }
                      verdict={
                        it.top_candidate
                          ? verdicts.get(it.top_candidate.listing.id)
                          : undefined
                      }
                      onToggle={() =>
                        it.top_candidate &&
                        toggleListing(it.top_candidate.listing.id)
                      }
                    />
                  </div>
                ));
              })()}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Slim progress bar + counter for the action-bar meta row.
 *
 *  Renders one line "N matched · scanned X of SCOPE of total TOTAL"
 *  followed by a gradient bar with a percent label. When ``scope`` is
 *  the same as ``total`` (no cap), the "of total" tail is dropped to
 *  avoid "scanned 8671 of 8671 of total 8671" repetition. */
function ScanProgress({
  scanning,
  matched,
  alreadyPlaced,
  processed,
  scope,
  total,
}: {
  scanning: boolean;
  matched: number;
  alreadyPlaced: number;
  processed: number;
  scope: number;
  total: number;
}) {
  const safeScope = Math.max(1, scope);
  const percent = Math.min(100, (processed / safeScope) * 100);
  const showAbsoluteTotal = scope < total;
  return (
    <div className="ml-1 flex items-center gap-2 min-w-0">
      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 whitespace-nowrap">
        {scanning && (
          <span
            className="inline-block w-2 h-2 rounded-full bg-accent-400 animate-pulse"
            aria-hidden
          />
        )}
        <span className="text-slate-300 font-medium">
          {matched.toLocaleString()}
        </span>{" "}
        matched
        {alreadyPlaced > 0 && (
          <>
            {" "}
            <span
              className="text-amber-300/90 font-medium"
              title={`${alreadyPlaced.toLocaleString()} already uploaded by your agency — pre-deselected`}
            >
              ({alreadyPlaced.toLocaleString()} already uploaded)
            </span>
          </>
        )}{" "}
        · {scanning ? "scanning" : "scanned"}{" "}
        <span className="text-slate-300 font-medium tabular-nums">
          {processed.toLocaleString()}
        </span>{" "}
        of{" "}
        <span className="text-slate-300 font-medium tabular-nums">
          {scope.toLocaleString()}
        </span>
        {showAbsoluteTotal && (
          <>
            {" "}
            of total{" "}
            <span className="text-slate-400 tabular-nums">
              {total.toLocaleString()}
            </span>
          </>
        )}
      </span>
      <div className="relative h-1.5 w-28 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent-500 to-accent-400 transition-[width] duration-300 ease-out"
          style={{ width: `${percent}%` }}
        />
        {scanning && (
          // A faint moving stripe overlay — sells "live" without
          // disturbing the underlying fill. Pure CSS via Tailwind's
          // animate-pulse on a translucent layer.
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"
            aria-hidden
          />
        )}
      </div>
      <span className="text-[11px] tabular-nums text-slate-400 w-9 text-right">
        {percent.toFixed(percent < 10 ? 1 : 0)}%
      </span>
    </div>
  );
}

/** Windowed match list — mounts only the rows currently scrolled into
 *  view (plus a small overscan) instead of one DOM node per match. On a
 *  10k-row scan the plain ``.map`` would mount tens of thousands of
 *  image-bearing nodes and lock the tab; this keeps the live node count
 *  flat (~viewport ÷ ROW_STRIDE).
 *
 *  Assumes a uniform row height (ROW_STRIDE) — which is why the caller
 *  drops the variable-height section dividers in this mode. Reads scroll
 *  state from the parent container passed via ``scrollParentRef`` rather
 *  than owning its own scrollbar, so it slots into the existing layout
 *  without nesting a second scroll region. */
function VirtualMatchList({
  items,
  selectedListings,
  verdicts,
  onToggle,
  showAgency,
  scrollParentRef,
}: {
  items: FinderItem[];
  selectedListings: Set<string>;
  verdicts: Map<string, Verdict>;
  onToggle: (id: string) => void;
  showAgency: boolean;
  scrollParentRef: RefObject<HTMLDivElement>;
}) {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);

  useEffect(() => {
    const el = scrollParentRef.current;
    if (!el) return;
    const sync = () => {
      setScrollTop(el.scrollTop);
      setViewportH(el.clientHeight);
    };
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", sync);
      ro.disconnect();
    };
  }, [scrollParentRef]);

  const overscan = 6;
  const total = items.length;
  const start = Math.max(0, Math.floor(scrollTop / ROW_STRIDE) - overscan);
  const visibleCount = Math.ceil(viewportH / ROW_STRIDE) + overscan * 2;
  const end = Math.min(total, start + visibleCount);

  const rows = [];
  for (let i = start; i < end; i++) {
    const it = items[i];
    const cid = it.top_candidate?.listing.id;
    rows.push(
      <div key={it.competitor_listing.id} style={{ height: ROW_STRIDE }} className="pb-1.5">
        <MatchRow
          item={it}
          showAgency={showAgency}
          selected={!!cid && selectedListings.has(cid)}
          verdict={cid ? verdicts.get(cid) : undefined}
          onToggle={() => cid && onToggle(cid)}
        />
      </div>,
    );
  }

  // Outer spacer holds the full scroll height; the inner layer is
  // offset to the first visible row so the window sits where the
  // scrollbar expects it.
  return (
    <div style={{ height: total * ROW_STRIDE, position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          transform: `translateY(${start * ROW_STRIDE}px)`,
        }}
      >
        {rows}
      </div>
    </div>
  );
}

function MatchRow({
  item,
  selected,
  verdict,
  onToggle,
  showAgency = false,
}: {
  item: FinderItem;
  selected: boolean;
  /** Portal liveness from the verify step; undefined until checked. */
  verdict?: Verdict;
  onToggle: () => void;
  showAgency?: boolean;
}) {
  const c = item.competitor_listing;
  const m = item.top_candidate?.listing;
  const score = item.top_candidate?.score ?? 0;
  const method = item.top_candidate?.method;
  const alreadyPlaced = m?.placement_id ?? null;
  const placedBy = m?.placed_by_user ?? null;
  const noCandidate = item.top_candidate === null;
  const gone = verdict === "gone";

  return (
    <label
      className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition cursor-pointer ${
        noCandidate
          ? "border-white/5 bg-white/[0.02] cursor-not-allowed opacity-60"
          : gone
            ? "border-rose-500/20 bg-rose-500/[0.04] cursor-not-allowed opacity-55 grayscale"
          : alreadyPlaced
            ? "border-amber-500/30 bg-amber-500/[0.06] hover:border-amber-500/50"
            : selected
              ? "border-accent-500/50 bg-accent-500/[0.06]"
              : "border-white/8 bg-white/[0.02] hover:border-white/15"
      }`}
    >
      <input
        type="checkbox"
        className="w-4 h-4 accent-accent-500"
        checked={selected}
        disabled={noCandidate || gone}
        onChange={onToggle}
      />
      {showAgency && (
        <div
          className="w-[88px] shrink-0 flex items-center gap-1.5 min-w-0"
          title={c.market_agency_name || "Unknown agency"}
        >
          {c.market_agency_logo_url ? (
            <img
              src={c.market_agency_logo_url}
              alt=""
              className="w-5 h-5 rounded object-cover border border-white/10 shrink-0 bg-white/5"
              loading="lazy"
            />
          ) : (
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ background: c.market_agency_color_hex || "#64748b" }}
            />
          )}
          <span className="text-[10px] text-slate-400 truncate">
            {c.market_agency_name || "—"}
          </span>
        </div>
      )}
      <ListingThumb listing={c} muted />
      <div className="text-slate-600 px-1">→</div>
      {m ? (
        <ListingThumb listing={m} />
      ) : (
        <div className="flex-1 text-xs text-slate-500 italic">
          No owner-direct match in our DB
        </div>
      )}
      {m && (
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {gone && (
            <span
              className="inline-flex items-center text-[10px] uppercase tracking-wider text-rose-200 bg-rose-500/15 border border-rose-500/30 rounded px-1.5 py-0.5"
              title="The owner's listing is no longer on the portal — skipped"
            >
              Deleted
            </span>
          )}
          {alreadyPlaced && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-200 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5"
              title={
                placedBy
                  ? `Already uploaded by ${placedBy.full_name || placedBy.email}`
                  : "Already uploaded by your agency"
              }
            >
              {placedBy && (
                <Avatar
                  url={placedBy.avatar_url}
                  name={placedBy.full_name}
                  email={placedBy.email}
                  size="xs"
                />
              )}
              <span>Already uploaded</span>
            </span>
          )}
          <span
            className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${
              method && CERTAIN_METHODS.has(method)
                ? "text-emerald-300 bg-emerald-500/15 border border-emerald-500/30"
                : "text-slate-300 bg-white/[0.06] border border-white/10"
            }`}
            title={item.top_candidate?.reasons.join(" · ")}
          >
            {(method && METHOD_LABELS[method]) || method}
          </span>
          <span className="text-[11px] tabular-nums font-semibold text-slate-200">
            {score}
          </span>
        </div>
      )}
    </label>
  );
}

function ListingThumb({
  listing,
  muted = false,
}: {
  listing: MarketListing;
  muted?: boolean;
}) {
  const price = listing.price_usd
    ? `$${Math.round(Number(listing.price_usd)).toLocaleString()}`
    : listing.price
      ? `${listing.price} ${listing.currency ?? ""}`
      : "—";
  const area = listing.area_sqm ? `${Math.round(Number(listing.area_sqm))} m²` : null;
  const where = listing.neighborhood || listing.district || "";
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {listing.cover_photo_url ? (
        <img
          src={listing.cover_photo_url}
          alt=""
          className="w-12 h-12 rounded object-cover border border-white/5 shrink-0"
          loading="lazy"
        />
      ) : (
        <div className="w-12 h-12 rounded bg-white/[0.04] border border-white/5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div
          className={`text-[12px] truncate ${muted ? "text-slate-400" : "text-slate-100"}`}
        >
          <a
            href={listing.published_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {listing.title || `${listing.platform} · ${listing.external_id}`}
          </a>
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {listing.platform.toUpperCase()} · {price}
          {area ? ` · ${area}` : ""}
          {listing.rooms ? ` · ${listing.rooms} rooms` : ""}
          {where ? ` · ${where}` : ""}
        </div>
      </div>
    </div>
  );
}
